import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactSelect, {
  components,
  FormatOptionLabelMeta,
  GroupBase,
  SelectComponentsConfig,
} from 'react-select';
import { AnyObject } from '@js/core/types/core-types';
import classNames from 'classnames';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import Button from '../../../core/components/button/button';
import { ButtonType } from '../../../core/components/button/button-enums';
import { SelectValue } from '../../../core/components/form-item/form-item-types';
import HTMLRender from '../../../core/components/html-render/html-render';
import Tooltip from '../../../core/components/tooltip/tooltip';
import { useLocale } from '../../../core/hooks/use-locale';
import { C_INPUT_BORDER } from '../../../core/utils/colors';
import { pxToRem } from '../../../core/utils/functions';
import { Icon, iconOnly } from '../../../core/utils/icons';
import { Z_INDEXES } from '../../../core/utils/variables';
import { ClientContext } from '../../client-context';

import { SelectProps } from './form-item-types';

const Select = <OPTION extends AnyObject, IsMulti extends boolean = false>({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  isRequired,
  isRequiredMarked,
  isDisabled,
  help,
  isClearable,
  isNarrow,
  isUpdatedMessage,
  placeholder,
  isMulti,
  options = [],
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
  onInputChange,
  onMenuScrollToBottom,
  isLoading,
  nonNullable,
  tooltip,
}: SelectProps<OPTION, IsMulti>) => {
  const id = useRef(uuidv4());
  const { Message } = useLocale();

  const { lockPageScroll, unlockPageScroll } = useContext(ClientContext);

  const [requiredMarked, setRequiredMarked] = useState(true);
  const [updatedMessage, setUpdatedMessage] = useState(false);

  useEffect(() => {
    if (isRequiredMarked !== undefined) {
      setRequiredMarked(isRequiredMarked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUpdatedMessage) {
      setUpdatedMessage(isUpdatedMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdatedMessage]);

  const handleChange = useCallback(
    (value: SelectValue<OPTION, IsMulti>) => {
      if (!value || (Array.isArray(value) && !value.length)) {
        nonNullable
          ? onChange((isMulti ? [] : {}) as SelectValue<OPTION, IsMulti>)
          : onChange(null);
      } else {
        onChange(value);
      }
    },
    [isMulti, nonNullable, onChange]
  );

  const customComponents = useMemo<
    Partial<SelectComponentsConfig<OPTION, IsMulti, GroupBase<OPTION>>>
  >(
    () => ({
      NoOptionsMessage: () => (
        <div
          className={classNames(
            'react-select__option',
            'react-select__option--is-disabled'
          )}
        >
          {Message.NO_OPTIONS}
        </div>
      ),
      LoadingMessage: () => (
        <div
          className={classNames(
            'react-select__option',
            'react-select__option--is-disabled'
          )}
        >
          {Message.LOADING}...
        </div>
      ),
      IndicatorSeparator: () => null,
      DropdownIndicator: (props) => (
        <components.DropdownIndicator
          className={iconOnly(Icon.ANGLE_DOWN)}
          {...props}
        >
          <>{null}</>
        </components.DropdownIndicator>
      ),
      ClearIndicator: () => (
        <Tooltip title={Message.DELETE}>
          <Button
            buttonType={ButtonType.CLEAR}
            disabled={isDisabled}
            aria-label={Message.DELETE_SELECT}
            onClick={() => handleChange(null)}
          ></Button>
        </Tooltip>
      ),
    }),
    [
      Message.DELETE,
      Message.DELETE_SELECT,
      Message.LOADING,
      Message.NO_OPTIONS,
      handleChange,
      isDisabled,
    ]
  );

  const formatOptionLabel = useCallback(
    ({ label, help }: OPTION, { context }: FormatOptionLabelMeta<OPTION>) => (
      <div>
        <HTMLRender html={label} />
        {help && context === 'menu' && (
          <div className="form-item__option-help">{help}</div>
        )}
      </div>
    ),
    []
  );

  return (
    <div
      className={classNames('select', {
        'form-item--error': error,
        'form-item--help': help,
        'form-item--disabled': isDisabled,
        'form-item--narrow': isNarrow,
      })}
    >
      {(label || help) && (
        <label htmlFor={id.current} className="form-item__label">
          {label}
          {requiredMarked && isRequired && typeof label === 'string' && (
            <span className="form-item__required">
              *<span className="screen-reader-only">{Message.REQUIRED2}</span>
            </span>
          )}
          {requiredMarked && !isRequired && typeof label === 'string' && (
            <span> {Message.OPTIONAL}</span>
          )}
          {updatedMessage && (
            <span className="screen-reader-only">{Message.UPDATE_MESSAGE}</span>
          )}
          {!!tooltip && (
            <div className="form-item__tooltip">
              <HTMLRender html={tooltip} />
            </div>
          )}
          {error ? (
            <div className="form-item__error">{error}</div>
          ) : help ? (
            <div className="form-item__help">{help}</div>
          ) : null}
        </label>
      )}
      <div className="select__wrapper">
        <ReactSelect
          inputId={id.current}
          name={name}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          value={_.isEmpty(value) ? null : value}
          onChange={handleChange as any}
          onBlur={onBlur}
          isDisabled={isDisabled}
          aria-disabled={isDisabled}
          isClearable={isDisabled === true ? false : isClearable}
          placeholder={placeholder}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          maxMenuHeight={300}
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onInputChange={onInputChange}
          onMenuScrollToBottom={onMenuScrollToBottom}
          isLoading={isLoading}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          onMenuOpen={lockPageScroll}
          onMenuClose={unlockPageScroll}
          className="react-select"
          classNamePrefix="react-select"
          components={customComponents}
          formatOptionLabel={formatOptionLabel}
          screenReaderStatus={({ count }) =>
            'Dostupné možnosti: ' + count + ' možností'
          }
          styles={{
            clearIndicator: () => ({}),
            container: () => ({}),
            control: () => ({}),
            dropdownIndicator: () => ({}),
            group: () => ({}),
            groupHeading: () => ({}),
            indicatorsContainer: () => ({}),
            indicatorSeparator: () => ({}),
            loadingIndicator: () => ({}),
            menu: (provided) => ({
              ...provided,
              margin: pxToRem([2, 0]),
              border: `${pxToRem(1)} solid ${C_INPUT_BORDER}`,
              borderRadius: pxToRem(3),
              overflow: 'hidden',
              boxShadow: 'unset',
            }),
            menuList: (provided) => ({
              ...provided,
              padding: 0,
            }),
            menuPortal: (provided) => ({
              ...provided,
              zIndex: Z_INDEXES.modal,
            }),
            multiValue: () => ({}),
            multiValueLabel: () => ({}),
            multiValueRemove: () => ({}),
            noOptionsMessage: () => ({}),
            option: () => ({}),
            placeholder: () => ({}),
            singleValue: () => ({}),
            valueContainer: () => ({}),
          }}
        />
      </div>
    </div>
  );
};

export default Select;
